import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { ArrowLeft, MailCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12 relative overflow-hidden" data-testid="forgot-password-page"
         style={{ background: "linear-gradient(135deg, hsl(var(--brand-wine)/0.08) 0%, hsl(var(--client)/0.08) 100%)" }}>
      <div className="blob" style={{ width: 320, height: 320, top: -60, right: -60, background: "hsl(var(--client) / 0.18)" }} />
      <div className="blob" style={{ width: 240, height: 240, bottom: -80, left: -40, background: "hsl(var(--brand-lime) / 0.18)" }} />

      <Card className="w-full max-w-md p-7 rounded-3xl border-2 relative z-10" data-testid="forgot-password-card">
        <div className="mb-5"><Logo /></div>

        {!sent ? (
          <>
            <h2 className="text-2xl font-display font-extrabold tracking-tight">Forgot your password?</h2>
            <p className="text-sm text-muted-foreground mb-5">
              No worries. Enter the email you signed up with and we'll send you a link to reset it.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-xs uppercase tracking-wider font-bold">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  autoFocus
                  data-testid="forgot-password-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="min-h-[48px] rounded-xl"
                />
              </div>

              <Button
                type="submit"
                disabled={busy || !email.trim()}
                data-testid="forgot-password-submit-btn"
                className="w-full min-h-[52px] rounded-full text-base font-bold"
                style={{ background: "hsl(var(--client))" }}
              >
                {busy ? "Sending…" : "Send reset link"}
              </Button>
            </form>
          </>
        ) : (
          <div className="text-center space-y-4 py-2" data-testid="forgot-password-success">
            <div className="mx-auto w-16 h-16 rounded-full flex items-center justify-center"
                 style={{ background: "hsl(var(--brand-lime) / 0.25)" }}>
              <MailCheck className="w-8 h-8" style={{ color: "hsl(var(--client))" }} />
            </div>
            <h2 className="text-xl font-display font-extrabold tracking-tight">Check your inbox</h2>
            <p className="text-sm text-muted-foreground">
              If <span className="font-mono font-semibold">{email}</span> matches an account, we've just sent
              a password reset link. It's good for the next <b>1 hour</b>.
            </p>
            <p className="text-xs text-muted-foreground">
              Didn't get it? Check your spam folder, or{" "}
              <button
                type="button"
                onClick={() => setSent(false)}
                data-testid="forgot-password-try-again-btn"
                className="underline font-semibold"
              >
                try again
              </button>.
            </p>
          </div>
        )}

        <div className="mt-6 pt-4 border-t text-center">
          <Link
            to="/login"
            data-testid="back-to-login-link"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-4 h-4" /> Back to sign in
          </Link>
        </div>
      </Card>
    </div>
  );
}
